import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { LOGIN } from '@/constants/testIds';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { BrandLogo, BrandBadge } from '@/components/BrandLogo';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('admin@hearclear.com');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/calendar" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate('/calendar');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-stone-100 flex items-stretch">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 text-white p-12" style={{ background: 'linear-gradient(135deg, #1E2860 0%, #2D3982 50%, #3A4AA0 100%)' }}>
        <div className="flex items-center gap-3">
          <div className="bg-white/95 rounded-lg px-3 py-2 shadow-lg">
            <BrandLogo height={30} />
          </div>
        </div>
        <div className="space-y-4">
          <h1 className="font-heading text-4xl font-bold leading-tight">
            One calendar.<br/>Every patient journey.
          </h1>
          <p className="text-white/80 max-w-md leading-relaxed">
            HearClear OS turns the calendar into your operating cockpit — appointments, lifecycles,
            clinical actions, and communications all in one workspace.
          </p>
          <ul className="text-sm text-white/70 space-y-1 pt-4">
            <li>· Enterprise-wide + clinic + audiologist calendars</li>
            <li>· 16-state appointment lifecycle with auditable events</li>
            <li>· Role-based visibility across the org hierarchy</li>
            <li>· WhatsApp, tasks & clinical actions from the calendar</li>
          </ul>
        </div>
        <div className="text-xs text-white/60">
          © 2026 HearClear · Central Operations Hub
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 border-stone-200 shadow-sm">
          <div className="mb-6">
            <div className="lg:hidden mb-4 flex items-center gap-2">
              <BrandBadge size={28} /><BrandLogo height={20} />
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight hc-section-title">Sign in</h2>
            <p className="text-sm text-stone-500 mt-1">Use your HearClear staff credentials.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-stone-600">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={LOGIN.emailInput}
                autoComplete="email"
                required
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-stone-600">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={LOGIN.passwordInput}
                autoComplete="current-password"
                required
                className="h-10"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid={LOGIN.submitButton}
              className="w-full h-10 hc-btn-primary"
              style={{ backgroundColor: '#2D3982' }}
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : 'Sign In'}
            </Button>
          </form>
          <div className="mt-6 pt-4 border-t border-stone-200 text-[11px] text-stone-400">
            Don't have a login? Ask your HearClear admin to create one.
          </div>
        </Card>
      </div>
    </div>
  );
}
